import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { openFeeReceiptPortal } from '../utils/documentPortalUrl';
import { useAuth } from '../context/AuthContext';
import {
  classifyFeeType,
  isFeeCategoryVisible,
  isFeeTypeVisible,
  isSchoolFeatureEnabled,
} from '../utils/schoolFeatures';
import { todayString, yearMonthString, formatMonthYear, formatDate } from '../utils/appDateTime';
import { Button } from './ui/button';
import { Input } from './ui/input';
import DateInput from './ui/date-input';
import MonthInput from './ui/month-input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { IndianRupee, Plus, Receipt, TrendingUp, LayoutList, Tag, Pencil, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Badge } from './ui/badge';
import useBranchLabels from '../hooks/useBranchLabels';
import BranchLabel from './ui/BranchLabel';

const Fees = () => {
  const { user, canManageFeature } = useAuth();
  const { showBranchLabels, formatLabel } = useBranchLabels();
  const canManageFees = canManageFeature('fees');
  const [feeTypes, setFeeTypes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [summary, setSummary] = useState(null);
  const [feesByMonth, setFeesByMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [structureDialogOpen, setStructureDialogOpen] = useState(false);
  const [structureEditId, setStructureEditId] = useState(null);
  const [feeTypeDialogOpen, setFeeTypeDialogOpen] = useState(false);
  const [feeTypeEditId, setFeeTypeEditId] = useState(null);
  const { toast } = useToast();

  const handleOpenReceipt = async (payment) => {
    try {
      await openFeeReceiptPortal(payment, api);
    } catch (error) {
      toast({
        title: 'Could not open receipt',
        description: error?.response?.data?.message || error?.message || 'Try again later',
        variant: 'destructive',
      });
    }
  };

  const [formData, setFormData] = useState({
    student_id: '', amount: '', discount: '0', late_fee: '0', payment_method: 'cash', remarks: '', fee_month: '', payment_date: todayString(),
  });
  const [structureForm, setStructureForm] = useState({
    class_id: '', fee_type_id: '', amount: '', due_date: '', late_fee: '0',
  });
  const [feeTypeForm, setFeeTypeForm] = useState({
    name: '', code: '', description: '', amount: '0', frequency: 'monthly', fee_category: 'standard',
  });

  const visibleFeeTypes = useMemo(
    () => feeTypes.filter((ft) => isFeeTypeVisible(user, ft)),
    [feeTypes, user]
  );

  const visibleFeeStructures = useMemo(
    () => feeStructures.filter((row) => {
      const category = row.fee_category || classifyFeeType({
        name: row.fee_type_name,
        code: row.fee_type_code,
      });
      return isFeeCategoryVisible(user, category);
    }),
    [feeStructures, user]
  );

  const feeCategoryOptions = useMemo(() => {
    const opts = [{ value: 'standard', label: 'Standard' }];
    if (isSchoolFeatureEnabled(user, 'library')) {
      opts.push({ value: 'library', label: 'Library' });
    }
    if (isSchoolFeatureEnabled(user, 'transport')) {
      opts.push({ value: 'transport', label: 'Transport' });
    }
    return opts;
  }, [user]);

  useEffect(() => { fetchData(); }, [user?.school_id]);

  useEffect(() => {
    if (selectedClassId) {
      const params = { class_id: selectedClassId };
      const academicYearId = getSelectedAcademicYearId();
      if (academicYearId) params.academic_year_id = academicYearId;
      const selectedClass = classes.find((c) => String(c.id) === String(selectedClassId));
      const schoolId = user?.school_id ?? selectedClass?.school_id;
      if (schoolId != null) params.school_id = schoolId;
      api.get('/fees/structure', { params })
        .then((res) => setFeeStructures(res.data.data || []))
        .catch(() => setFeeStructures([]));
    } else {
      setFeeStructures([]);
    }
  }, [selectedClassId, user?.school_id, classes]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      fetchData();
      if (selectedClassId) {
        const params = { class_id: selectedClassId };
        const academicYearId = getSelectedAcademicYearId();
        if (academicYearId) params.academic_year_id = academicYearId;
        const selectedClass = classes.find((c) => String(c.id) === String(selectedClassId));
        const schoolId = user?.school_id ?? selectedClass?.school_id;
        if (schoolId != null) params.school_id = schoolId;
        api.get('/fees/structure', { params })
          .then((res) => setFeeStructures(res.data.data || []))
          .catch(() => setFeeStructures([]));
      }
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id, selectedClassId, classes]);

  const fetchData = async () => {
    const academicYearId = getSelectedAcademicYearId();
    const schoolId = user?.school_id;
    const params = {
      ...(schoolId ? { school_id: schoolId } : {}),
      ...(academicYearId ? { academic_year_id: academicYearId } : {}),
    };
    const typeParams = user?.school_id ? { school_id: user.school_id } : {};
    try {
      const results = await Promise.allSettled([
        api.get('/fees/types', { params: typeParams }),
        api.get('/fees/payments', { params }),
        api.get('/students', { params }),
        api.get('/classes', { params }),
        api.get('/fees/summary', { params }),
        api.get('/fees/by-month', { params }),
      ]);

      const [
        typesRes,
        paymentsRes,
        studentsRes,
        classesRes,
        summaryRes,
        feesByMonthRes,
      ] = results;

      if (typesRes.status === 'fulfilled') {
        setFeeTypes(typesRes.value.data.data || []);
      }
      if (paymentsRes.status === 'fulfilled') {
        setPayments(paymentsRes.value.data.data || []);
      }
      if (studentsRes.status === 'fulfilled') {
        setStudents(studentsRes.value.data.data || []);
      }
      if (classesRes.status === 'fulfilled') {
        let classList = classesRes.value.data.data || [];
        if (academicYearId && classList.length === 0) {
          try {
            const fallback = await api.get('/classes', { params: schoolId ? { school_id: schoolId } : {} });
            classList = fallback.data.data || [];
          } catch (e) {
            // ignore fallback errors
          }
        }
        setClasses(classList);
      }
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value.data.data?.summary || {});
      }
      if (feesByMonthRes.status === 'fulfilled') {
        const feesData = feesByMonthRes.value.data.data || [];
        console.log('Fees by month data:', feesData);
        setFeesByMonth(feesData);
      }
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedClassId) return;
    if (classes.length > 0 && !classes.some((c) => String(c.id) === String(selectedClassId))) {
      setSelectedClassId('');
    }
  }, [classes, selectedClassId]);

  const handleCollectFee = async (e) => {
    e.preventDefault();
    try {
      // Ensure fee_month is provided - it's required!
      if (!formData.fee_month || !formData.fee_month.match(/^\d{4}-\d{2}$/)) {
        toast({ 
          title: 'Error', 
          description: 'Please select a valid fee month and year (e.g., April 2025)', 
          variant: 'destructive' 
        });
        return;
      }
      
      const feeMonth = formData.fee_month; // Use the selected month, don't default
      const paymentDate = formData.payment_date || todayString(); // Default to today if not set
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        discount: parseFloat(formData.discount) || 0,
        late_fee: parseFloat(formData.late_fee) || 0,
        student_id: parseInt(formData.student_id) || 0,
        fee_month: feeMonth, // This is the month/year the fee is FOR (e.g., April 2025)
        payment_date: paymentDate, // This is when the payment was actually made (can be backdated)
      };
      
      console.log('Submitting fee payment:', payload); // Debug log
      
      const response = await api.post('/fees/collect', payload);
      const monthLabel = formatMonthYear(feeMonth);
      toast({ 
        title: 'Fee Submitted Successfully', 
        description: `Fee for ${monthLabel} has been submitted. Receipt: ${response.data.data.receipt_no}. The fee by month section will update shortly.` 
      });
      setIsDialogOpen(false);
      setFormData({ student_id: '', amount: '', discount: '0', late_fee: '0', payment_method: 'cash', remarks: '', fee_month: '', payment_date: todayString() });
      // Refresh data after payment to update fee by month section
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      console.error('Fee collection error:', error); // Debug log
      const errorMessage = error.response?.data?.message || 'Failed to submit fee';
      // Check if it's a duplicate error (409 status)
      if (error.response?.status === 409) {
        toast({ 
          title: 'Duplicate Payment Detected', 
          description: errorMessage + ' Please select a different month.', 
          variant: 'destructive' 
        });
      } else {
        toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
      }
    }
  };

  const handleSaveFeeStructure = async (e) => {
    e.preventDefault();
    try {
      const selectedClass = classes.find((c) => String(c.id) === String(structureForm.class_id));
      const schoolId = user?.school_id ?? selectedClass?.school_id;
      if (structureEditId) {
        const academicYearId = getSelectedAcademicYearId();
        await api.put(`/fees/structure/${structureEditId}`, {
          amount: structureForm.amount,
          due_date: structureForm.due_date || null,
          late_fee: structureForm.late_fee || 0,
          academic_year_id: academicYearId || null,
          ...(schoolId != null ? { school_id: schoolId } : {}),
        });
        toast({ title: 'Success', description: 'Fee structure updated' });
      } else {
        const academicYearId = getSelectedAcademicYearId();
        await api.post('/fees/structure', {
          class_id: structureForm.class_id,
          fee_type_id: structureForm.fee_type_id,
          amount: structureForm.amount,
          due_date: structureForm.due_date || null,
          late_fee: structureForm.late_fee || 0,
          academic_year_id: academicYearId || null,
          ...(schoolId != null ? { school_id: schoolId } : {}),
        });
        toast({ title: 'Success', description: 'Fee added to class' });
      }
      setStructureDialogOpen(false);
      setStructureEditId(null);
      setStructureForm({ class_id: selectedClassId || '', fee_type_id: '', amount: '', due_date: '', late_fee: '0' });
      fetchData();
      if (selectedClassId) {
        const p = { class_id: selectedClassId };
        const academicYearId = getSelectedAcademicYearId();
        if (academicYearId) p.academic_year_id = academicYearId;
        const selectedClass = classes.find((c) => String(c.id) === String(selectedClassId));
        const schoolId = user?.school_id ?? selectedClass?.school_id;
        if (schoolId != null) p.school_id = schoolId;
        api.get('/fees/structure', { params: p }).then((r) => setFeeStructures(r.data.data || []));
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleDeleteFeeStructure = async (id) => {
    if (!window.confirm('Remove this fee from the class structure?')) return;
    try {
      await api.delete(`/fees/structure/${id}`);
      toast({ title: 'Success', description: 'Removed from structure' });
      fetchData();
      if (selectedClassId) {
        const p = { class_id: selectedClassId };
        const academicYearId = getSelectedAcademicYearId();
        if (academicYearId) p.academic_year_id = academicYearId;
        const selectedClass = classes.find((c) => String(c.id) === String(selectedClassId));
        const schoolId = user?.school_id ?? selectedClass?.school_id;
        if (schoolId != null) p.school_id = schoolId;
        api.get('/fees/structure', { params: p }).then((r) => setFeeStructures(r.data.data || []));
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleSaveFeeType = async (e) => {
    e.preventDefault();
    try {
      if (feeTypeEditId) {
        await api.put(`/fees/types/${feeTypeEditId}`, feeTypeForm);
        toast({ title: 'Success', description: 'Fee type updated' });
      } else {
        await api.post('/fees/types', feeTypeForm);
        toast({ title: 'Success', description: 'Fee type created' });
      }
      setFeeTypeDialogOpen(false);
      setFeeTypeEditId(null);
      setFeeTypeForm({ name: '', code: '', description: '', amount: '0', frequency: 'monthly', fee_category: 'standard' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleDeleteFeeType = async (id) => {
    if (!window.confirm('Delete this fee type? It may be used in fee structure.')) return;
    try {
      await api.delete(`/fees/types/${id}`);
      toast({ title: 'Success', description: 'Fee type deleted' });
      fetchData();
      if (selectedClassId) {
        const p = { class_id: selectedClassId };
        const academicYearId = getSelectedAcademicYearId();
        if (academicYearId) p.academic_year_id = academicYearId;
        const selectedClass = classes.find((c) => String(c.id) === String(selectedClassId));
        const schoolId = user?.school_id ?? selectedClass?.school_id;
        if (schoolId != null) p.school_id = schoolId;
        api.get('/fees/structure', { params: p }).then((r) => setFeeStructures(r.data.data || []));
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  // Get all payments with fee_month to check paid status
  const paidMonthsByStudent = React.useMemo(() => {
    const map = new Map();
    (payments || []).forEach((payment) => {
      if (payment.status === 'paid' && payment.fee_month) {
        const key = `${payment.student_id}_${payment.fee_month}`;
        map.set(key, {
          student_id: payment.student_id,
          fee_month: payment.fee_month,
          amount: payment.total_amount || payment.amount,
          payment_date: payment.payment_date,
        });
      }
    });
    return map;
  }, [payments]);

  return (
    <div className="space-y-6" data-testid="fees-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Fee Management</h1>
          <p className="text-muted-foreground">Collect and manage student fees</p>
        </div>
        {canManageFees && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="collect-fee-btn">
              <Plus className="w-4 h-4 mr-2" /> Collect Fee
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border text-foreground">
            <DialogHeader>
              <DialogTitle>Collect Fee</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCollectFee} className="space-y-4">
              <div>
                <Label>Student *</Label>
                <Select value={formData.student_id} onValueChange={(v) => {
                  const studentData = feesByMonth.find(s => String(s.student_id) === String(v));
                  const firstUnpaidMonth = studentData?.months.find(m => m.status === 'overdue' || m.status === 'not_due');
                  // If no unpaid month, default to current month
                  const defaultMonth = firstUnpaidMonth?.year_month || yearMonthString();
                  const defaultAmount = firstUnpaidMonth ? String(firstUnpaidMonth.amount || 0) : formData.amount;
                  setFormData({
                    ...formData, 
                    student_id: v,
                    fee_month: defaultMonth,
                    amount: defaultAmount
                  });
                }}>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select student" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border max-h-48">
                    {students.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>
                        {formatLabel(`${s.first_name} ${s.last_name}`.trim(), s)} ({s.admission_no})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Fee Month - Prominent placement right after student */}
              <div className="bg-blue-600/10 border-2 border-blue-600/50 rounded-lg p-4">
                <Label className="text-base font-semibold text-blue-300 flex items-center gap-2">
                  <span>Fee Month & Year *</span>
                  {formData.fee_month && (
                    <Badge className="bg-blue-600/30 text-blue-300 border-blue-500/50">
                      {formatMonthYear(formData.fee_month)}
                    </Badge>
                  )}
                </Label>
                <p className="text-xs text-muted-foreground mb-2">Select the month for which you are collecting this fee (e.g., April 2025). Already paid months are hidden.</p>
                {formData.student_id ? (
                  <Select 
                    value={formData.fee_month} 
                    onValueChange={(v) => {
                      const studentData = feesByMonth.find(s => String(s.student_id) === String(formData.student_id));
                      const selectedMonth = studentData?.months.find(m => m.year_month === v);
                      setFormData({
                        ...formData, 
                        fee_month: v,
                        amount: selectedMonth ? String(selectedMonth.amount || 0) : formData.amount
                      });
                    }}
                    required
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder="Select month and year (e.g., April 2025)" />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border max-h-64">
                      {(() => {
                        // Get all months for selected student
                        const studentData = feesByMonth.find(s => String(s.student_id) === String(formData.student_id));
                        if (!studentData) {
                          // If student not in feesByMonth, show current month and next few months
                          const months = [];
                          const currentDate = new Date();
                          for (let i = 0; i < 6; i++) {
                            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
                            const yearMonth = yearMonthString(date);
                            const label = formatMonthYear(yearMonth);
                            months.push(
                              <SelectItem key={yearMonth} value={yearMonth}>
                                {label} {i === 0 ? '(Current)' : ''}
                              </SelectItem>
                            );
                          }
                          return months;
                        }
                        
                        // Separate months by status - only show unpaid/overdue/future, hide already paid to prevent duplicates
                        const unpaidMonths = studentData.months.filter(m => m.status === 'overdue' || m.status === 'not_due');
                        // Don't show paid months in dropdown to prevent duplicate payments
                        
                        // Generate future months (up to 3 months ahead)
                        const futureMonths = [];
                        const lastMonth = studentData.months[studentData.months.length - 1];
                        if (lastMonth) {
                          const lastDate = new Date(lastMonth.year_month + '-01');
                          for (let i = 1; i <= 3; i++) {
                            const futureDate = new Date(lastDate.getFullYear(), lastDate.getMonth() + i, 1);
                            const yearMonth = yearMonthString(futureDate);
                            const label = formatMonthYear(yearMonth);
                            futureMonths.push({ year_month: yearMonth, month_label: label, status: 'not_due' });
                          }
                        }
                        
                        return (
                          <>
                            {/* Unpaid/Overdue months first */}
                            {unpaidMonths.length > 0 && (
                              <>
                                {unpaidMonths.length > 0 && (
                                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-b border-border">
                                    Unpaid / Overdue
                                  </div>
                                )}
                                {unpaidMonths.map(month => (
                                  <SelectItem key={month.year_month} value={month.year_month}>
                                    {month.month_label} {month.status === 'overdue' ? '— Overdue' : '— Not Due'}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                            
                            {/* Future months */}
                            {futureMonths.length > 0 && (
                              <>
                                {(unpaidMonths.length > 0 || paidMonthsByStudent.size > 0) && (
                                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-b border-border mt-1">
                                    Future Months
                                  </div>
                                )}
                                {futureMonths.map(month => (
                                  <SelectItem key={month.year_month} value={month.year_month}>
                                    {month.month_label} — Not Due
                                  </SelectItem>
                                ))}
                              </>
                            )}
                            
                            {/* Fallback if no months found */}
                            {unpaidMonths.length === 0 && futureMonths.length === 0 && (
                              <SelectItem value={yearMonthString()}>
                                {formatMonthYear(yearMonthString())} (Current)
                              </SelectItem>
                            )}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                ) : (
                  <MonthInput 
                    value={formData.fee_month || yearMonthString()} 
                    onChange={(v) => setFormData({...formData, fee_month: v})}
                    className="bg-muted border-border" 
                    required 
                    disabled
                  />
                )}
                <p className="text-muted-foreground text-xs mt-1">
                  {formData.student_id 
                    ? 'Select the month and year (e.g., April 2025) for which this fee is being paid. Unpaid/overdue months are shown first.' 
                    : 'Please select a student first'}
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Amount (₹) *</Label>
                  <Input type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="bg-muted border-border" required />
                </div>
                <div>
                  <Label>Discount (₹)</Label>
                  <Input type="number" step="0.01" min="0" value={formData.discount} onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    className="bg-muted border-border" />
                </div>
                <div>
                  <Label>Late Fee (₹)</Label>
                  <Input type="number" step="0.01" min="0" value={formData.late_fee} onChange={(e) => setFormData({...formData, late_fee: e.target.value})}
                    className="bg-muted border-border" placeholder="0" />
                </div>
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Date *</Label>
                <DateInput 
                  value={formData.payment_date || todayString()} 
                  onChange={(v) => setFormData({...formData, payment_date: v})}
                  className="bg-muted border-border" 
                  required
                  max={todayString()}
                />
                <p className="text-muted-foreground text-xs mt-1">
                  Select the date when the fee was actually paid. Defaults to today. You can backdate for late entries.
                </p>
              </div>
              <div>
                <Label>Remarks</Label>
                <Input value={formData.remarks} onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  className="bg-muted border-border" />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Collect Fee</Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Fee Type dialog (add/edit) */}
      {canManageFees && (
      <Dialog open={feeTypeDialogOpen} onOpenChange={setFeeTypeDialogOpen}>
        <DialogContent className="border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle>{feeTypeEditId ? 'Edit Fee Type' : 'Add Fee Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveFeeType} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={feeTypeForm.name} onChange={(e) => setFeeTypeForm({ ...feeTypeForm, name: e.target.value })}
                className="bg-muted border-border" placeholder="e.g. Admission, Exam, Tuition" required />
            </div>
            <div>
              <Label>Code (optional)</Label>
              <Input value={feeTypeForm.code} onChange={(e) => setFeeTypeForm({ ...feeTypeForm, code: e.target.value })}
                className="bg-muted border-border" placeholder="e.g. ADM, EXAM" />
            </div>
            {feeCategoryOptions.length > 1 && (
            <div>
              <Label>Category</Label>
              <Select
                value={feeTypeForm.fee_category || 'standard'}
                onValueChange={(v) => setFeeTypeForm({ ...feeTypeForm, fee_category: v })}
              >
                <SelectTrigger className="bg-muted border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {feeCategoryOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label>Frequency</Label>
              <Select value={feeTypeForm.frequency} onValueChange={(v) => setFeeTypeForm({ ...feeTypeForm, frequency: v })}>
                <SelectTrigger className="bg-muted border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  <SelectItem value="one_time">One-time</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default Amount (₹)</Label>
              <Input type="number" min="0" step="0.01" value={feeTypeForm.amount} onChange={(e) => setFeeTypeForm({ ...feeTypeForm, amount: e.target.value })}
                className="bg-muted border-border" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={feeTypeForm.description} onChange={(e) => setFeeTypeForm({ ...feeTypeForm, description: e.target.value })}
                className="bg-muted border-border" placeholder="e.g. Annual exam fee" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setFeeTypeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{feeTypeEditId ? 'Update' : 'Add'} Fee Type</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Fee Structure dialog (add/edit) */}
      {canManageFees && (
      <Dialog open={structureDialogOpen} onOpenChange={setStructureDialogOpen}>
        <DialogContent className="border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle>{structureEditId ? 'Edit Fee for Class' : 'Add Fee for Class'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveFeeStructure} className="space-y-4">
            {!structureEditId && (
              <div>
                <Label>Class *</Label>
                <Select value={structureForm.class_id} onValueChange={(v) => setStructureForm({ ...structureForm, class_id: v })} required>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!structureEditId && (
              <div>
                <Label>Fee Type *</Label>
                <Select value={structureForm.fee_type_id} onValueChange={(v) => setStructureForm({ ...structureForm, fee_type_id: v })} required>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select fee type" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {visibleFeeTypes.map((ft) => (
                      <SelectItem key={ft.id} value={String(ft.id)}>{formatLabel(`${ft.name} (${ft.frequency})`, ft)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Amount (₹) *</Label>
              <Input type="number" min="0" step="0.01" value={structureForm.amount} onChange={(e) => setStructureForm({ ...structureForm, amount: e.target.value })}
                className="bg-muted border-border" required />
            </div>
            <div>
              <Label>Due Date (optional)</Label>
              <DateInput value={structureForm.due_date} onChange={(v) => setStructureForm({ ...structureForm, due_date: v })}
                className="bg-muted border-border" />
            </div>
            <div>
              <Label>Late Fee (₹)</Label>
              <Input type="number" min="0" step="0.01" value={structureForm.late_fee} onChange={(e) => setStructureForm({ ...structureForm, late_fee: e.target.value })}
                className="bg-muted border-border" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setStructureDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{structureEditId ? 'Update' : 'Add'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                      <IndianRupee className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Collected</p>
                <p className="text-2xl font-bold text-foreground">
                  ₹{(summary?.total_collected || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Receipts</p>
                <p className="text-2xl font-bold text-foreground">{summary?.total_receipts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fee Types</p>
                <p className="text-2xl font-bold text-foreground">{visibleFeeTypes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fee Types – dynamic types (Admission, Exam, Tuition, etc.) */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Tag className="w-5 h-5 text-amber-500" />
            Fee Types
          </CardTitle>
          <p className="text-muted-foreground text-sm font-normal">
            Define all fee types (e.g. Admission, Exam, Tuition, Library). These are used when setting fee structure per class.
          </p>
        </CardHeader>
        <CardContent>
          {canManageFees && (
          <div className="flex justify-end mb-4">
            <Button
              size="sm"
              className="bg-muted hover:bg-muted/80"
              onClick={() => {
                setFeeTypeEditId(null);
                setFeeTypeForm({ name: '', code: '', description: '', amount: '0', frequency: 'monthly', fee_category: 'standard' });
                setFeeTypeDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Fee Type
            </Button>
          </div>
          )}
          {loading ? (
            <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
          ) : visibleFeeTypes.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">No fee types yet. Add types like Admission, Exam, Tuition, etc.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-left">
                    <th className="px-4 py-3 text-foreground font-medium">Name</th>
                    <th className="px-4 py-3 text-foreground font-medium">Code</th>
                    <th className="px-4 py-3 text-foreground font-medium">Frequency</th>
                    <th className="px-4 py-3 text-foreground font-medium text-right">Default Amount</th>
                    <th className="px-4 py-3 text-foreground font-medium w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFeeTypes.map((ft) => (
                    <tr key={ft.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 text-foreground font-medium">{ft.name}</td>
                      <td className="px-4 py-3 text-foreground">{ft.code || '—'}</td>
                      <td className="px-4 py-3 text-foreground">{ft.frequency || 'monthly'}</td>
                      <td className="px-4 py-3 text-right text-foreground">₹{Number(ft.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        {canManageFees ? (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setFeeTypeEditId(ft.id);
                              setFeeTypeForm({
                                name: ft.name || '',
                                code: ft.code || '',
                                description: ft.description || '',
                                amount: String(ft.amount ?? 0),
                                frequency: ft.frequency || 'monthly',
                                fee_category: classifyFeeType(ft),
                              });
                              setFeeTypeDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-400"
                            onClick={() => handleDeleteFeeType(ft.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">View only</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fee Structure – per class */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <LayoutList className="w-5 h-5 text-blue-500" />
            Fee Structure by Class
          </CardTitle>
          <p className="text-muted-foreground text-sm font-normal">
            Select a class and add fee types with amounts. This defines what fees apply to each class (e.g. Class 1: Tuition ₹2000, Exam ₹500).
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="min-w-[180px]">
              <Label className="text-muted-foreground">Class</Label>
              <Select value={selectedClassId} onValueChange={setSelectedClassId}>
                <SelectTrigger className="bg-muted border-border mt-1">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedClassId && canManageFees && (
              <Button
                className="bg-blue-600 hover:bg-blue-700 mt-6"
                onClick={() => {
                  setStructureEditId(null);
                  setStructureForm({
                    class_id: selectedClassId,
                    fee_type_id: '',
                    amount: '',
                    due_date: '',
                    late_fee: '0',
                  });
                  setStructureDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Fee for this Class
              </Button>
            )}
          </div>
          {!selectedClassId ? (
            <p className="text-muted-foreground py-6">Select a class to view and manage its fee structure.</p>
          ) : loading ? (
            <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
          ) : visibleFeeStructures.length === 0 ? (
            <p className="text-muted-foreground py-6">No fees defined for this class. Click &quot;Add Fee for this Class&quot; to add.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-left">
                    <th className="px-4 py-3 text-foreground font-medium">Fee Type</th>
                    <th className="px-4 py-3 text-foreground font-medium">Frequency</th>
                    <th className="px-4 py-3 text-foreground font-medium text-right">Amount (₹)</th>
                    <th className="px-4 py-3 text-foreground font-medium">Due Date</th>
                    <th className="px-4 py-3 text-foreground font-medium text-right">Late Fee</th>
                    <th className="px-4 py-3 text-foreground font-medium w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFeeStructures.map((row) => (
                    <tr key={row.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 text-foreground font-medium">{row.fee_type_name}</td>
                      <td className="px-4 py-3 text-foreground">{row.fee_type_frequency || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium text-foreground">₹{Number(row.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-foreground">{row.due_date ? formatDate(row.due_date) : '—'}</td>
                      <td className="px-4 py-3 text-right text-foreground">₹{Number(row.late_fee || 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        {canManageFees ? (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setStructureEditId(row.id);
                              setStructureForm({
                                class_id: selectedClassId,
                                fee_type_id: row.fee_type_id,
                                amount: String(row.amount || 0),
                                due_date: row.due_date || '',
                                late_fee: String(row.late_fee || 0),
                              });
                              setStructureDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-400"
                            onClick={() => handleDeleteFeeStructure(row.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">View only</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default Fees;
